<script setup lang="ts">
/**
 * The keyboard-shortcut reference of an app: groups („Allgemein“, „Liste“)
 * of rows — what it does, and the keys as caps. An item's `keys` is one combo
 * (`['Mod', 'K']`) or several alternatives (`[['J'], ['ArrowDown']]`, shown
 * with „oder“). A search field filters by the description and the keys;
 * groups without a hit disappear.
 *
 * `v-model:open`; closes by ✕, Escape or backdrop. The notation follows the
 * platform (⌘ on a Mac, Strg elsewhere) unless `apple` forces one.
 */
import { computed, ref, watch } from 'vue';
import { NxSearchInput } from '../Inputs';
import { NxModal } from '../Modal';
import NxKbd from './NxKbd.vue';
import { formatCombo, isApplePlatform, parseCombo, type NxKeyCombo } from './keys';

export interface NxShortcutItem {
  label: string;
  /** One combo (`'Mod+K'` or `['Mod', 'K']`), or several alternatives. */
  keys: string | NxKeyCombo | readonly NxKeyCombo[];
}

export interface NxShortcutGroup {
  title: string;
  items: readonly NxShortcutItem[];
}

const props = withDefaults(
  defineProps<{
    groups: readonly NxShortcutGroup[];
    title?: string;
    /** Show the search field. Defaults to on from ten shortcuts. */
    searchable?: boolean;
    searchPlaceholder?: string;
    emptyLabel?: string;
    /** Word between alternative combos. */
    orLabel?: string;
    apple?: boolean;
  }>(),
  {
    title: 'Tastenkürzel',
    searchable: undefined,
    searchPlaceholder: 'Suche',
    emptyLabel: 'Kein Kürzel gefunden.',
    orLabel: 'oder',
    apple: undefined,
  }
);

const open = defineModel<boolean>('open', { required: true });
const emit = defineEmits<{ close: [] }>();

const query = ref('');
watch(open, (isOpen) => {
  if (isOpen) query.value = '';
});

const isApple = computed(() => props.apple ?? isApplePlatform());

function alternatives(item: NxShortcutItem): NxKeyCombo[] {
  if (typeof item.keys === 'string') return [parseCombo(item.keys)];
  const keys = item.keys as readonly unknown[];
  return keys.length && Array.isArray(keys[0]) ? (keys as NxKeyCombo[]) : [item.keys as NxKeyCombo];
}

const total = computed(() => props.groups.reduce((n, g) => n + g.items.length, 0));
const showSearch = computed(() => props.searchable ?? total.value >= 10);

const visibleGroups = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.groups;
  return props.groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const keysText = alternatives(item)
          .map((combo) => formatCombo(combo, isApple.value))
          .join(' ');
        return (
          item.label.toLowerCase().includes(q) ||
          keysText.toLowerCase().includes(q) ||
          group.title.toLowerCase().includes(q)
        );
      }),
    }))
    .filter((group) => group.items.length);
});
</script>

<template>
  <NxModal
    v-model:open="open"
    class="nx-shortcuts-dialog"
    :title="props.title"
    size="wide"
    @close="emit('close')"
  >
    <template #body>
      <div class="nx-shortcuts-dialog__body">
        <NxSearchInput
          v-if="showSearch"
          v-model="query"
          class="nx-shortcuts-dialog__search"
          :placeholder="props.searchPlaceholder"
          :aria-label="props.searchPlaceholder"
          data-testid="nx-shortcuts-search"
        />
        <div v-if="visibleGroups.length" class="nx-shortcuts-dialog__groups">
          <section
            v-for="group in visibleGroups"
            :key="group.title"
            class="nx-shortcuts-dialog__group"
            :aria-label="group.title"
          >
            <h3 class="nx-shortcuts-dialog__group-title">{{ group.title }}</h3>
            <dl class="nx-shortcuts-dialog__list">
              <div
                v-for="item in group.items"
                :key="item.label"
                class="nx-shortcuts-dialog__row"
                data-testid="nx-shortcut-row"
              >
                <dt class="nx-shortcuts-dialog__label">{{ item.label }}</dt>
                <dd class="nx-shortcuts-dialog__keys">
                  <template v-for="(combo, i) in alternatives(item)" :key="i">
                    <span v-if="i > 0" class="nx-shortcuts-dialog__or">{{ props.orLabel }}</span>
                    <NxKbd :keys="combo" size="sm" :apple="isApple" />
                  </template>
                </dd>
              </div>
            </dl>
          </section>
        </div>
        <p v-else class="nx-shortcuts-dialog__empty" role="status">{{ props.emptyLabel }}</p>
      </div>
    </template>
  </NxModal>
</template>

<style scoped>
.nx-shortcuts-dialog__body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  padding-block-end: var(--nxt-space-xs);
}
.nx-shortcuts-dialog__groups {
  columns: 2 calc(var(--nxt-size-unit) * 17);
  column-gap: var(--nxt-space-xl);
}
.nx-shortcuts-dialog__group {
  break-inside: avoid;
  margin-block-end: var(--nxt-space-lg);
}
.nx-shortcuts-dialog__group-title {
  margin: 0 0 var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.nx-shortcuts-dialog__list {
  margin: 0;
}
.nx-shortcuts-dialog__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-md);
  padding: var(--nxt-space-xs) var(--nxt-space-2xs);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-shortcuts-dialog__row:last-child {
  border-block-end: 0;
}
.nx-shortcuts-dialog__label {
  min-width: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
}
.nx-shortcuts-dialog__keys {
  display: inline-flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  margin: 0;
}
.nx-shortcuts-dialog__or {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-shortcuts-dialog__empty {
  margin: 0;
  padding: var(--nxt-space-xl) 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
</style>
